import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    makeStyles,
    Snackbar,
    TextField,
    Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import { useIntl } from 'react-intl'

import { CapeeshColors } from '../../../assets/ColorPalette'
import { i18nMessages } from '../../../Logic/CodeAccessible.i18n.Messages'
import { userService } from '../../../_state/services'
import { InviteRolesToDisplayName } from '../../../Logic/OrganizationAssetConstants'

const useStyles = makeStyles(() => ({
    root: {
        maxWidth: '1500px',
        margin: '0 auto',
        padding: '30px 25px 60px',
    },
    pageTitle: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '24px',
    },
    pageDescription: {
        fontFamily: 'Rubik',
        fontSize: '14px',
        color: CapeeshColors.Gray1,
        marginTop: '4px',
        maxWidth: '820px',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '24px',
    },
    search: {
        minWidth: '320px',
    },
    hint: {
        fontFamily: 'Rubik',
        fontSize: '12px',
        color: CapeeshColors.Gray1,
        marginTop: '4px',
    },
    counts: {
        fontFamily: 'Rubik',
        fontSize: '13px',
        color: CapeeshColors.Gray1,
        textAlign: 'right',
    },
    tableContainer: {
        marginTop: '20px',
        borderRadius: '12px',
        border: `1px solid ${CapeeshColors.Gray4}`,
    },
    headerCell: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '13px',
        color: CapeeshColors.Gray1,
        whiteSpace: 'nowrap',
    },
    primaryCell: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '14px',
    },
    secondaryCell: {
        fontFamily: 'Rubik',
        fontSize: '13px',
        color: CapeeshColors.Gray1,
    },
    badge: {
        marginLeft: '6px',
        height: '20px',
    },
    actionCell: {
        whiteSpace: 'nowrap',
    },
    spinnerWrap: {
        display: 'flex',
        justifyContent: 'center',
        padding: '60px',
    },
    warning: {
        marginTop: '12px',
    },
}))

// Mirrors the backend SchoolRole enum, which serialises as a number.
const SchoolRole = {
    Student: 0,
    Editor: 1,
    Teacher: 2,
    Admin: 3,
    Tutor: 4,
    Translator: 5,
    SuperAdmin: 6,
    Reporting: 7,
    QA: 8,
}

// Privilege order, so sorting by access type puts Capeesh Admin above QA Tester rather than
// sorting them alphabetically. Mirrors AuthenticationTypes.OrderedSchoolRoles on the backend.
const SCHOOL_ROLE_RANK = [
    SchoolRole.SuperAdmin,
    SchoolRole.Admin,
    SchoolRole.Teacher,
    SchoolRole.Reporting,
    SchoolRole.QA,
    SchoolRole.Translator,
    SchoolRole.Editor,
    SchoolRole.Tutor,
    SchoolRole.Student,
]

const SCHOOL_ROLE_NAME = {
    [SchoolRole.Student]: 'Student',
    [SchoolRole.Editor]: 'Editor',
    [SchoolRole.Teacher]: 'Teacher',
    [SchoolRole.Admin]: 'Admin',
    [SchoolRole.Tutor]: 'Tutor',
    [SchoolRole.Translator]: 'Translator',
    [SchoolRole.SuperAdmin]: 'Super Admin',
    [SchoolRole.Reporting]: 'Reporting',
    [SchoolRole.QA]: 'QA',
}

// The dashboard has its own vocabulary: backend Admin reads "Capeesh Admin", Reporting reads
// "Administrator". Falls back to the raw name for the legacy roles that have no label.
const roleDisplayName = (schoolRole) => {
    const backendName = SCHOOL_ROLE_NAME[schoolRole]
    return InviteRolesToDisplayName[backendName] || backendName || String(schoolRole)
}

const rankOfRole = (schoolRole) => {
    const index = SCHOOL_ROLE_RANK.indexOf(schoolRole)
    return index === -1 ? SCHOOL_ROLE_RANK.length : index
}

const COLUMNS = [
    { id: 'organizationName', messageKey: 'administratorAccessColumnOrganization' },
    { id: 'email', messageKey: 'administratorAccessColumnEmail' },
    { id: 'schoolRole', messageKey: 'administratorAccessColumnAccessType' },
]

/**
 * Every administrator in every organization. Capeesh super admins only.
 *
 * The list is one row per (user, organization, role), so someone who administers three
 * organizations appears three times and can be revoked from one of them without touching the rest.
 */
const OverallAdministrators = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const { user } = props

    const [overview, setOverview] = useState(null)
    const [loading, setLoading] = useState(true)
    const [searchToken, setSearchToken] = useState('')
    const [orderBy, setOrderBy] = useState('organizationName')
    const [order, setOrder] = useState('asc')
    const [pendingRevoke, setPendingRevoke] = useState(null)
    const [revoking, setRevoking] = useState(false)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

    const isSuperAdmin = user?.isSuperAdmin === true

    const showError = (error, fallbackMessage) => {
        const serverMessage = typeof error?.response?.data === 'string' ? error.response.data : null
        setSnackbar({ open: true, message: serverMessage || fallbackMessage, severity: 'error' })
    }

    const loadOverview = async () => {
        setLoading(true)
        try {
            const response = await userService.getAllAdministrators()
            setOverview(response.data)
        } catch (error) {
            console.error('Failed to load administrators', error)
            showError(error, intl.formatMessage(i18nMessages.capeeshAccessLoadFailed))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!isSuperAdmin) {
            return
        }
        loadOverview()
    }, [isSuperAdmin])

    const rows = overview?.rows || []

    const visibleRows = useMemo(() => {
        // Email only, deliberately. Names repeat and are often blank, and matching organization
        // names too would mix "people at Acme" with "people called Acme".
        const token = searchToken.trim().toLowerCase()
        const filtered = token ? rows.filter((row) => (row.email || '').toLowerCase().includes(token)) : [...rows]

        const direction = order === 'asc' ? 1 : -1

        return filtered.sort((a, b) => {
            if (orderBy === 'schoolRole') {
                const byRank = rankOfRole(a.schoolRole) - rankOfRole(b.schoolRole)
                if (byRank !== 0) {
                    return direction * byRank
                }
                return (a.organizationName || '').localeCompare(b.organizationName || '')
            }

            const comparison = (a[orderBy] || '').localeCompare(b[orderBy] || '')
            if (comparison !== 0) {
                return direction * comparison
            }
            return (a.email || '').localeCompare(b.email || '')
        })
    }, [rows, searchToken, order, orderBy])

    const handleSort = (columnId) => {
        if (orderBy === columnId) {
            setOrder(order === 'asc' ? 'desc' : 'asc')
            return
        }
        setOrderBy(columnId)
        setOrder('asc')
    }

    const handleRevoke = async () => {
        const target = pendingRevoke
        if (!target) {
            return
        }

        setRevoking(true)
        try {
            const response =
                target.scope === 'organization'
                    ? await userService.revokeAdministratorAccessForOrganization({
                          organizationId: target.row.organizationId,
                          userId: target.row.userId,
                      })
                    : await userService.revokeAllAdministratorAccess({ userId: target.row.userId })

            const leftWithout = response?.data?.organizationsLeftWithoutCapeeshAdmin || []
            setSnackbar({
                open: true,
                severity: leftWithout.length > 0 ? 'warning' : 'success',
                message:
                    leftWithout.length > 0
                        ? intl.formatMessage(i18nMessages.administratorAccessRevokedLeftWithout, {
                              organizations: leftWithout.join(', '),
                          })
                        : intl.formatMessage(i18nMessages.administratorAccessRevoked),
            })

            setPendingRevoke(null)
            loadOverview()
        } catch (error) {
            console.error('Failed to revoke administrator access', error)
            showError(error, intl.formatMessage(i18nMessages.capeeshAccessLoadFailed))
        } finally {
            setRevoking(false)
        }
    }

    if (!isSuperAdmin) {
        return <div className={classes.root} />
    }

    const isSelf = (row) => row.userId === user?.info?.id

    const renderConfirmBody = () => {
        if (!pendingRevoke) {
            return null
        }

        const { row, scope } = pendingRevoke

        return scope === 'organization'
            ? intl.formatMessage(i18nMessages.administratorAccessRevokeOrganizationBody, {
                  name: row.name || row.email,
                  role: roleDisplayName(row.schoolRole),
                  organization: row.organizationName,
              })
            : intl.formatMessage(i18nMessages.administratorAccessRevokeAllBody, {
                  name: row.name || row.email,
              })
    }

    return (
        <div className={classes.root}>
            <Typography className={classes.pageTitle}>
                {intl.formatMessage(i18nMessages.administratorAccessTitle)}
            </Typography>
            <Typography className={classes.pageDescription}>
                {intl.formatMessage(i18nMessages.administratorAccessDescription)}
            </Typography>

            {loading && (
                <div className={classes.spinnerWrap}>
                    <CircularProgress />
                </div>
            )}

            {!loading && overview && (
                <React.Fragment>
                    <div className={classes.toolbar}>
                        <div>
                            <TextField
                                className={classes.search}
                                label={intl.formatMessage(i18nMessages.administratorAccessSearchLabel)}
                                value={searchToken}
                                onChange={(event) => setSearchToken(event.target.value)}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Typography className={classes.hint}>
                                {intl.formatMessage(i18nMessages.administratorAccessSearchHint)}
                            </Typography>
                        </div>

                        <div className={classes.counts}>
                            <Typography className={classes.counts}>
                                {intl.formatMessage(i18nMessages.administratorAccessSummary, {
                                    administrators: overview.numberOfAdministrators,
                                    organizations: overview.numberOfOrganizations,
                                })}
                            </Typography>
                            <Typography className={classes.counts}>
                                {intl.formatMessage(i18nMessages.administratorAccessShowingCount, {
                                    shown: visibleRows.length,
                                    total: rows.length,
                                })}
                            </Typography>
                        </div>
                    </div>

                    {/* Rows without an email can never match the search, so say so rather than
                        letting them silently disappear from a page about who has access. */}
                    {searchToken.trim() !== '' && overview.numberOfRowsWithoutEmail > 0 && (
                        <Alert severity="info" className={classes.warning}>
                            {intl.formatMessage(i18nMessages.administratorAccessNoEmailNote, {
                                count: overview.numberOfRowsWithoutEmail,
                            })}
                        </Alert>
                    )}

                    <TableContainer className={classes.tableContainer}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Typography className={classes.headerCell}>
                                            {intl.formatMessage(i18nMessages.administratorAccessColumnUser)}
                                        </Typography>
                                    </TableCell>
                                    {COLUMNS.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            sortDirection={orderBy === column.id ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : 'asc'}
                                                onClick={() => handleSort(column.id)}>
                                                <Typography className={classes.headerCell}>
                                                    {intl.formatMessage(i18nMessages[column.messageKey])}
                                                </Typography>
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleRows.map((row) => (
                                    <TableRow key={`${row.userId}_${row.organizationId}_${row.schoolRole}`} hover>
                                        <TableCell>
                                            <Typography className={classes.primaryCell}>
                                                {row.name}
                                                {row.isSuperAdmin && (
                                                    <Chip
                                                        className={classes.badge}
                                                        size="small"
                                                        label={intl.formatMessage(
                                                            i18nMessages.administratorAccessSuperAdminBadge
                                                        )}
                                                    />
                                                )}
                                                {row.hasAllOrganizationsReporting && (
                                                    <Chip
                                                        className={classes.badge}
                                                        size="small"
                                                        variant="outlined"
                                                        label={intl.formatMessage(
                                                            i18nMessages.administratorAccessAllOrgReportingBadge
                                                        )}
                                                    />
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography className={classes.secondaryCell}>
                                                {row.organizationName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography className={classes.secondaryCell}>
                                                {row.email || (
                                                    <em>
                                                        {intl.formatMessage(
                                                            i18nMessages.administratorAccessNoEmailPlaceholder
                                                        )}
                                                    </em>
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography className={classes.secondaryCell}>
                                                {roleDisplayName(row.schoolRole)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" className={classes.actionCell}>
                                            {isSelf(row) ? (
                                                <Typography className={classes.secondaryCell}>
                                                    {intl.formatMessage(
                                                        i18nMessages.administratorAccessCannotRevokeSelf
                                                    )}
                                                </Typography>
                                            ) : (
                                                <React.Fragment>
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            setPendingRevoke({ row, scope: 'organization' })
                                                        }>
                                                        {intl.formatMessage(
                                                            i18nMessages.administratorAccessRevokeOrganization
                                                        )}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => setPendingRevoke({ row, scope: 'all' })}>
                                                        {intl.formatMessage(
                                                            i18nMessages.administratorAccessRevokeAll
                                                        )}
                                                    </Button>
                                                </React.Fragment>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {visibleRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={COLUMNS.length + 2} align="center">
                                            <Typography className={classes.secondaryCell}>
                                                {intl.formatMessage(i18nMessages.administratorAccessNoRows)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </React.Fragment>
            )}

            <Dialog open={pendingRevoke !== null} onClose={() => setPendingRevoke(null)}>
                <DialogTitle>
                    {pendingRevoke?.scope === 'organization'
                        ? intl.formatMessage(i18nMessages.administratorAccessRevokeOrganizationTitle)
                        : intl.formatMessage(i18nMessages.administratorAccessRevokeAllTitle)}
                </DialogTitle>
                <DialogContent>
                    <Typography>{renderConfirmBody()}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingRevoke(null)} disabled={revoking}>
                        {intl.formatMessage(i18nMessages.capeeshAccessCancel)}
                    </Button>
                    <Button onClick={handleRevoke} color="secondary" variant="contained" disabled={revoking}>
                        {revoking ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            intl.formatMessage(i18nMessages.capeeshAccessConfirm)
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

const mapStateToProps = (state) => ({
    user: state.user,
})

export default connect(mapStateToProps)(OverallAdministrators)
